export const en = {
  app: {
    title: 'StudyQuantifier',
    subtitle: 'Quantified study and deliberate practice'
  },
  common: {
    start: 'Start',
    stop: 'Stop',
    pause: 'Pause',
    resume: 'Resume',
    cancel: 'Cancel',
    save: 'Save',
    discard: 'Discard',
    confirm: 'Confirm',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    close: 'Close',
    expand: 'Expand',
    collapse: 'Collapse',
    continue: 'Continue',
    yes: 'Yes',
    no: 'No',
    loading: 'Loading…',
    error: 'Error',
    retry: 'Retry',
    search: 'Search',
    filter: 'Filter',
    all: 'All',
    none: 'None'
  },
  themes: {
    label: 'Theme',
    dark: 'Dark',
    oled: 'OLED',
    light: 'Light'
  },
  session: {
    modeA: 'Deliberate practice',
    modeB: 'Active recall',
    start: 'Start session',
    end: 'End session',
    duration: 'Duration',
    focus: 'Focus',
    break: 'Break',
    readingTime: 'Reading time',
    distractions: 'Distractions',
    frustration: 'Frustration',
    energy: 'Energy'
  },
  bootstrap: {
    greeting: 'Bootstrap Phase 1 operational.',
    counter: 'Counter'
  },
  macroTimer: {
    title: 'Study block',
    stateInactive: 'Ready to start',
    stateFocusRunning: 'Focus in progress',
    stateFocusPaused: 'Focus paused',
    stateBreakRunning: 'Break in progress',
    stateBreakPaused: 'Break paused',
    statePendingFinish: 'Block finished',
    stateFinished: 'Block ended',
    stateDiscarded: 'Block discarded',
    stateOpenEnded: 'Open block running',
    actionStart: 'Start block',
    actionPause: 'Pause',
    actionResume: 'Resume',
    actionFinish: 'Finish',
    actionConfirmFinish: 'Confirm finish',
    actionResumeStudying: 'Keep studying',
    actionDiscard: 'Discard',
    actionNewSession: 'New session',
    reading: 'Reading',
    distractions: 'Distractions',
    addDistraction: '+1 distraction',
    addReading: 'Add reading',
    readingMinutesShort: '{n} min'
  },
  pomodoro: {
    statusIdle: 'Idle',
    statusFocusRunning: 'Focus running',
    statusFocusPaused: 'Focus paused',
    statusBreakRunning: 'Break running',
    statusBreakPaused: 'Break paused',
    statusPendingFinish: 'Ready to finish',
    statusFinished: 'Session complete',
    statusDiscarded: 'Session discarded',
    timeRemaining: '{min} min remaining',
    progressPct: '{pct}% complete',
    controlPause: 'Pause',
    controlResume: 'Resume',
    controlFinish: 'Finish block',
    controlReset: 'Reset session',
    controlDistraction: '+1 distraction',
    controlReadingStart: 'Start reading',
    controlReadingPause: 'Pause reading',
    readingFooterLabel: 'Reading',
    resetTitle: 'Reset session?',
    resetIntro: "You'll lose {list}.",
    resetNothingToLose: 'There is nothing to lose yet.',
    resetProblems: '{n} problem',
    resetProblemsPlural: '{n} problems',
    resetFocus: '{min} min of focus',
    resetConjectures: '{n} conjecture',
    resetConjecturesPlural: '{n} conjectures',
    resetConjunction: ' and ',
    resetHint: "This can't be undone.",
    resetCancel: 'Cancel',
    resetConfirm: 'Reset'
  },
  problemTimer: {
    problemNumber: 'Problem',
    hintIdle: 'Press SPACE to start',
    hintRunning: 'Solving in blind focus…',
    hintEvaluating: 'Stopped',
    nextHint: 'N for next · ESC to discard',
    timePlaceholder: '—:—.—',
    blindModeOn: 'Blind mode: on',
    blindModeOff: 'Blind mode: off',
    conjectureHint: 'Saved with the current chain (subject → subtopic → book → chapter → section). If a problem timer is running, its number is attached too.',
    evaluatingLabel: 'Classifying',
    readingLabel: 'Reading',
    conjectureLabel: 'Conjecture',
    conjectureToggle: 'Add conjecture',
    conjectureShow: 'Show conjecture field',
    conjectureHide: 'Hide conjecture field',
    conjecturePlaceholder: 'Capture an insight, a question, a hunch…',
    conjectureSave: 'Save',
    conjectureSaveFull: 'Save conjecture',
    timesUp: "Time's up"
  },
  shortcuts: {
    title: 'Keyboard shortcuts',
    intro: 'All shortcuts work when focus is not inside a text field.',
    hint: 'Customize them from Preferences.',
    close: 'Close',
    openLabel: 'View keyboard shortcuts (?)',
    problemToggle: 'Start / stop problem',
    problemNext: 'Next problem',
    problemDiscard: 'Discard attempt',
    rate1: 'Mark as correct',
    rate2: 'Mark as incorrect',
    rate3: 'Mark as unresolved',
    macroStart: 'Start session',
    macroPause: 'Pause session',
    macroResume: 'Resume session',
    macroFinish: 'Finish session',
    none: '—'
  },
  recentProblems: {
    ariaLabel: 'Recent problems',
    fallbackTitle: 'Recent problems',
    empty: 'Your first problem will appear here. Press SPACE to start.',
    modePractice: 'Practice',
    modeRecall: 'Recall',
    colNumber: '#',
    colTime: 'Time',
    colResult: 'Result',
    resultCorrect: 'Correct',
    resultIncorrect: 'Incorrect',
    resultUnresolved: 'Unresolved',
    groupOpen: 'Open session',
    groupClose: 'Close session'
  },
  rating: {
    correct: 'Correct',
    incorrect: 'Incorrect',
    unresolved: 'Unresolved',
    ariaLabel: 'Attempt rating',
    hint: 'Rate the attempt with keys 1, 2 or 3 — or tap a button.',
    shortcut1: 'key 1',
    shortcut2: 'key 2',
    shortcut3: 'key 3',
    notesLabel: 'Note',
    notesPlaceholder: 'What did you learn from this attempt?',
    taxonomyLabel: 'Root cause',
    taxonomyNone: 'Unspecified',
    next: 'Next problem',
    nextDisabled: 'Rate the attempt to continue',
    discard: 'Discard',
    discardedToast: 'Problem discarded',
    discardedUndo: 'Undo',
    needRating: 'Rate the attempt to continue'
  },
  modeB: {
    title: 'Active recall',
    groupsTitle: 'Review groups',
    groupsEmpty: 'No pending reviews',
    groupsEmptyHint: 'You have no items due for review. Come back later.',
    groupsCounter: '{groups} groups · {items} items',
    groupSingular: 'group',
    groupPlural: 'groups',
    itemSingular: 'item',
    itemPlural: 'items',
    groupsHint: 'Pick a group to start',
    backToGroups: 'Back to groups',
    groupItems: '{n} items',
    mostUrgent: 'Most urgent',
    pickGroup: 'Pick a group to start',
    contextLabel: 'Original problem',
    itemCounter: 'Item {current} of {total}',
    problemLabel: 'Problem',
    hintIdle: 'Press SPACE to start',
    hintRunning: 'Solving blind…',
    hintEvaluating: 'Stopped',
    correct: 'Good',
    incorrect: 'Bad',
    consulted: 'I checked the solution',
    consultedHint: 'Check this if you had to look at the solution to answer.',
    ariaLabel: 'Review rating',
    exitConfirmTitle: 'Exit review?',
    exitConfirmBody: "If you exit now, you'll lose the current item's progress without saving.",
    next: 'Next',
    finish: 'Finish review',
    finishGroup: 'Finish group',
    exit: 'Exit',
    noDue: 'No problems due',
    needRating: 'Rate the item to continue'
  },
  prepare: {
    title: 'Prepare session',
    subject: 'Subject',
    subtopic: 'Subtopic',
    book: 'Book',
    chapter: 'Chapter',
    section: 'Section',
    chooseSubject: 'Choose a subject',
    chooseSubtopic: 'Choose a subtopic',
    chooseBook: 'Choose a book',
    chooseChapter: 'Choose a chapter',
    chooseSection: 'Choose a section',
    addSubtopic: '+ New subtopic',
    empty: 'No subjects yet',
    emptyHint: 'Create your first subject to start quantifying your study.',
    createFirst: 'Create first subject',
    addSubject: '+ New subject',
    addBook: '+ New book',
    addChapter: '+ New chapter',
    addSection: '+ New section',
    nameLabel: 'Name',
    create: 'Create',
    cancel: 'Cancel',
    optional: 'optional',
    startBlock: 'Start block',
    yourSession: 'Your session',
    needSubject: 'Choose a subject to start',
    requiredLegend: '* Required field',
    optionalLegend: 'Other fields are optional — retention inherits automatically.',
    reviewDue: 'Review',
    reviewDueCount: '{n} due',
    duration: 'Block duration',
    durationOpen: 'Open',
    durationManual: 'Custom',
    durationMinutes: '{n} min',
    durationOpenHint: 'Runs until you stop it'
  },
  summary: {
    title: 'Good session!',
    subtitle: 'Log how you felt to fine-tune your schedule.',
    frustrationHelp: 'How hard it was to stay focused in this session.',
    energyHelp: 'Mental energy level at the end of the session.',
    focus: 'Focus time',
    reading: 'Reading time',
    distractions: 'Distractions',
    attemptsTitle: 'Attempts',
    correct: 'Correct',
    incorrect: 'Incorrect',
    unresolved: 'Unresolved',
    noAttempts: 'No attempts recorded',
    averageProblem: 'Average per problem',
    frustration: 'Frustration',
    energy: 'Energy',
    sliderHint: '0 = none · 10 = extreme',
    save: 'Save session',
    saving: 'Saving…',
    keepStudying: 'Keep studying',
    discard: 'Discard',
    confirmDiscard: 'Confirm discard',
    discardWarning: 'This will permanently delete all attempts from this session.',
    cancel: 'Cancel'
  },
  locale: {
    label: 'Language',
    en: 'English',
    es: 'Español'
  },
  sync: {
    open: 'Sync',
    guest: 'Sign in to enable sync',
    running: 'Syncing…',
    never: 'Not synced yet',
    lastAt: 'Last sync: {when}',
    justNow: 'just now',
    minutesAgo: '{n} min ago',
    hoursAgo: '{n}h ago',
    daysAgo: '{n}d ago',
    error: 'Sync error: {msg}',
    pending: '{n} pending',
    conflicts: '{n} conflicts'
  },
  quarantine: {
    title: 'Quarantine',
    close: 'Close',
    open: 'Open quarantine',
    empty: 'No quarantined operations.',
    emptyHint: 'Failed sync operations will appear here for manual recovery.',
    opsLabel: 'ops',
    retry: 'Retry',
    discard: 'Discard',
    retryAll: 'Retry all',
    discardAll: 'Discard all',
    flashRetriedOne: 'Operation queued for sync.',
    flashDiscardedOne: 'Operation discarded.',
    flashRetriedMany: '{n} operations queued for sync.',
    flashDiscardedMany: '{n} operations discarded.',
    bannerNew: '{n} operations in quarantine. Review them.',
    bannerReview: 'Review'
  },
  orphan: {
    title: 'Unfinished session found',
    body: 'There is an active session from {when}. Resume it or discard it?',
    resume: 'Resume',
    discard: 'Discard',
    justNow: 'just now',
    minutesAgo: '{n} min ago',
    hoursAgo: '{n} h ago',
    daysAgo: '{n} d ago'
  },
  crossTab: {
    followerBanner: 'Another tab is the primary window. Sessions and sync are managed there.',
    followerBlockedStart: 'Start a session in the primary tab.',
    syncViaMaster: 'Sync requested from primary window',
    becomePrimary: 'Open this tab in the foreground',
    takeOver: 'Take over',
    takingOver: 'Taking over…',
    masterBusy: 'Primary window has an active session. Close it first.',
    nowMaster: 'This tab is now primary.'
  },
  migration: {
    title: 'Migrate guest data',
    intro: 'We found data created while you were using the app as a guest. You can migrate it to your account or discard it permanently.',
    statsTitle: 'Will be migrated',
    subjects: '{n} subjects',
    subtopics: '{n} subtopics',
    books: '{n} books',
    chapters: '{n} chapters',
    sections: '{n} sections',
    sessions: '{n} sessions',
    attempts: '{n} attempts',
    recalls: '{n} recall items',
    reviews: '{n} review events',
    conjectures: '{n} conjectures',
    sleep: '{n} sleep records',
    total: '{n} items total',
    migrate: 'Migrate to my account',
    migrating: 'Migrating…',
    discard: 'Discard guest data',
    discardWarning: 'This will permanently delete all guest data. This cannot be undone.',
    confirmDiscard: 'Yes, delete permanently',
    cancel: 'Cancel',
    error: 'Migration failed: {msg}',
    done: 'Migration complete'
  },
  fsrsAdvanced: {
    title: 'Advanced FSRS settings',
    close: 'Close',
    loading: 'Loading…',
    globalTitle: 'Global defaults',
    globalHint: 'Applies to any subject without its own override. Lower retention means longer intervals.',
    retentionLabel: 'Target retention',
    intervalLabel: 'Maximum interval',
    saveGlobal: 'Save global',
    saved: 'Global settings saved.',
    subjectsTitle: 'Subjects',
    subjectsHint: 'Override per subject is set from the gear icon in Prepare Session. Reset here to fall back to global.',
    noSubjects: 'No subjects yet.',
    noSubjectsHint: 'Create a subject to configure per-subject FSRS overrides.',
    colName: 'Subject',
    colRetention: 'Retention',
    colInterval: 'Max int.',
    override: 'Override',
    inherited: 'Global',
    resetOverride: 'Reset override'
  },
  subjectSettings: {
    title: 'Subject settings',
    close: 'Close',
    loading: 'Loading…',
    retention: 'Target retention',
    retentionHint: 'How likely you want to remember a problem when its review comes due. Higher means shorter intervals.',
    save: 'Save',
    cancel: 'Cancel',
    saved: 'Settings saved.',
    deleteTitle: 'Delete node',
    deleteIntro: 'Will delete "{name}" and all its dependent items.',
    deleteHint: 'Already-logged problems are preserved. You can undo from the toast.',
    confirmDelete: 'Delete',
    advanced: 'Advanced',
    maximumInterval: 'Maximum interval',
    maximumIntervalHint: 'Upper cap between reviews. Lower if you study every day, higher if you study weekly.',
    subtopics: 'Subtopics',
    overrideActive: 'Override',
    overrideInactive: 'Inherit',
    retentionShort: 'Retention',
    intervalShort: 'Max int.',
    daysSuffix: 'd',
    definedHere: 'Defined here',
    inheritedFrom: 'Inherited from {source}',
    resetOverride: 'Reset to inherit',
    sourceSubject: 'the subject',
    sourceSubtopic: 'the subtopic',
    sourceBook: 'the book',
    sourceChapter: 'the chapter',
    sourceSection: 'the section',
    sourceGlobal: 'global settings',
    sourceDefault: 'default',
    childSubtopics: 'Subtopics',
    childBooks: 'Books',
    childChapters: 'Chapters',
    childSections: 'Sections',
    noChildren: 'No direct children.'
  },
  avatar: {
    sectionTitle: 'Profile picture',
    sectionHint: 'Shown next to your name on the leaderboard. Max 5 MB. Resized automatically.',
    upload: 'Choose image',
    remove: 'Remove',
    uploaded: 'Profile picture updated.',
    removed: 'Profile picture removed.',
    tooLarge: 'Image is too large (max 5 MB).',
    invalidType: 'Only PNG, JPG, WEBP or GIF.',
    failed: 'Upload failed. Try again.',
    current: 'Current picture'
  },
  push: {
    sectionTitle: 'Notifications',
    sectionHint: 'Get a reminder when you have reviews due. Requires permission from your browser.',
    toggle: 'Enable reminders',
    reminderHour: 'Reminder time',
    hourSuffix: ':00',
    unsupported: 'This browser does not support notifications.',
    enabled: 'Reminders enabled.',
    disabled: 'Reminders disabled.',
    permissionDenied: 'Permission denied. Enable notifications in your browser settings.',
    failed: 'Could not enable reminders. Try again.',
    saved: 'Reminder time updated.'
  },
  leaderboard: {
    open: 'Open leaderboard',
    panel: {
      title: 'Leaderboard',
      close: 'Close',
      metricMinutes: 'Total hours',
      metricProblems: 'Problems today',
      metricStreak: 'Streak',
      tabGlobal: 'Global',
      tabCountry: 'Country: {code}',
      tabFriends: 'Friends',
      tabGroup: 'Group: {name}',
      metricTotalProblems: 'Total problems',
      metricRetention: 'Retention',
      noGroups: 'No groups yet.',
      friendsEmpty: 'Add friends to see them here.',
      friendsEmptyHint: 'Study with others and compare your progress.',
      groupEmpty: 'No entries in this group yet.',
      groupEmptyHint: 'Wait for members to log their sessions.',
      emptyHint: 'Keep studying and you will appear in the ranking.',
      colRank: '#',
      colUser: 'User',
      colCountry: 'Country',
      colProblemsToday: 'Today',
      colMinutesToday: 'Time today',
      colStreak: 'Streak',
      colTotal: 'Total',
      colRetention: 'Retention',
      loadMore: 'Load more',
      loading: 'Loading…',
      empty: 'No entries yet.',
      errorLoad: 'Could not load the leaderboard.',
      youLabel: 'You'
    },
    sectionTitle: 'Leaderboard',
    sectionHint: 'Share your aggregate stats with other users. You can turn it off at any time.',
    optIn: 'Show me on the leaderboard',
    displayName: 'Display name',
    displayNamePlaceholder: '3-24 characters',
    displayNameTaken: 'This name is already in use. Try another one.',
    nameTooShort: 'At least 3 characters.',
    nameTooLong: 'At most 24 characters.',
    nameInvalidChars: 'Letters, numbers, spaces, hyphens, underscores only.',
    nameReserved: 'This name is reserved.',
    nameOffensive: 'This name is not allowed.',
    country: 'Country',
    countryPirate: 'Pirate',
    countryHint: 'Shown next to your name. Choose Pirate to hide your real country.',
    optInRequiresName: 'Choose a display name before opting in.',
    saved: 'Leaderboard settings saved.',
    disabled: 'Leaderboard disabled. Your entry was removed.',
    nameCooldown: 'You can change your name again after {date}.',
    nameCooldownShort: 'Name change available after {date}.',
    nameSaved: 'Name updated.'
  },
  groups: {
    title: 'Groups',
    close: 'Close',
    loading: 'Loading…',
    create: 'Create group',
    createTitle: 'New group',
    creating: 'Creating…',
    createError: 'Could not create the group.',
    invalidName: 'Name must be between 1 and 40 characters.',
    name: 'Group name',
    namePlaceholder: 'e.g. Physics hard mode',
    join: 'Join by code',
    joinTitle: 'Join a group',
    joining: 'Joining…',
    joinError: 'Could not join the group.',
    invalidCode: 'That code does not exist.',
    alreadyMember: 'You are already in that group.',
    code: 'Invite code',
    codePlaceholder: 'ABCD1234',
    copyCode: 'Click to copy',
    copied: 'Copied!',
    noGroups: 'You are not in any group yet.',
    owner: 'Owner',
    member: 'member',
    members: 'members',
    leave: 'Leave',
    delete: 'Delete',
    confirmLeave: 'Leave?',
    confirmDelete: 'Delete for everyone?',
    confirmYes: 'Yes',
    cancel: 'Cancel'
  },
  friends: {
    title: 'Friends',
    close: 'Close',
    loading: 'Loading…',
    tabIncoming: 'Incoming',
    tabOutgoing: 'Sent',
    tabFriends: 'Friends',
    add: 'Add',
    addTitle: 'Add friend',
    addHint: 'Search by exact display name. Up to 5 results.',
    searchPlaceholder: 'Display name',
    search: 'Search',
    searching: 'Searching…',
    noResults: 'No users found with that name.',
    sendRequest: 'Send request',
    sending: 'Sending…',
    sentOk: 'Request sent.',
    sentAlready: 'A request is already pending.',
    sentError: 'Could not send request.',
    accept: 'Accept',
    decline: 'Decline',
    cancel: 'Cancel',
    remove: 'Remove',
    noIncoming: 'No incoming requests.',
    noOutgoing: 'No outgoing requests.',
    noFriends: 'No friends yet.',
    unknown: 'Unknown user'
  },
  foreignQueues: {
    title: 'Operations from other accounts',
    intro: 'This device has pending operations from {n} other account(s). Nothing is deleted automatically.',
    empty: 'No foreign operations.',
    emptyHint: 'Queued operations from other accounts will appear here.',
    close: 'Close',
    opsLabel: 'ops',
    export: 'Export',
    move: 'Move to this account',
    discard: 'Discard',
    flashExported: 'Queue exported.',
    flashMoved: '{n} operations moved to this account.',
    flashDiscarded: '{n} operations discarded.',
    confirmDiscardInline: 'Discard {n} ops ({bytes})? This cannot be undone.',
    yesDiscard: 'Yes, discard',
    no: 'No',
    moveTitle: 'Move operations to this account',
    moveBody: 'This will move {n} operations ({bytes}) to the current account. If the same data already exists on the server, duplicates will be created.',
    moveAck: 'I understand duplicates may appear.',
    moveConfirm: 'Move',
    cancel: 'Cancel'
  },
  auth: {
    pendingQueue: {
      title: 'Pending operations',
      body: 'This account has {n} operations pending sync ({bytes}). They will stay on this device after you sign out. Sign back in with the same account to sync them, or export them now.',
      cancel: 'Cancel',
      leave: 'Sign out, leave them here',
      export: 'Export and sign out'
    },
    title: 'Account',
    signedInAs: 'Signed in as',
    signIn: 'Sign in',
    signUp: 'Create account',
    signOut: 'Sign out',
    email: 'Email',
    password: 'Password',
    passwordHint: 'At least 6 characters',
    submitSignIn: 'Sign in',
    submitSignUp: 'Create account',
    switchToSignUp: 'No account? Create one',
    switchToSignIn: 'Already have an account? Sign in',
    busy: 'Working…',
    errorInvalidCredentials: 'Incorrect email or password.',
    errorEmailInUse: 'That email is already registered.',
    errorWeakPassword: 'Password is too short.',
    errorGeneric: 'Something went wrong. Try again.',
    guestBanner: 'You are using the app as a guest. Sign in to sync across devices.',
    open: 'Account'
  },
  shell: {
    skipToContent: 'Skip to content',
    brand: 'StudyQuantifier',
    nav: {
      studySection: 'Study',
      session: 'Session',
      history: 'History',
      progressSection: 'Progress',
      analytics: 'Analytics',
      forecast: 'Forecast',
      checklist: 'Checklist',
      communitySection: 'Community',
      leaderboard: 'Leaderboard',
      friends: 'Friends',
      groups: 'Groups',
      personalSection: 'Personal',
      goals: 'Goals',
      conjectures: 'Conjectures',
      sleep: 'Sleep',
      systemSection: 'System',
      ariaSidebar: 'Main navigation',
      ariaBottomNav: 'Main navigation',
      preferences: 'Preferences',
      quarantine: 'Quarantine',
    },
    metrics: {
      today: 'Today',
      streak: 'Streak',
      dueToday: 'Due today',
    },
    user: {
      authenticated: 'Account',
      guest: 'Guest · Sign in',
    },
    mobile: {
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      more: 'More',
    },
    sync: {
      idle: 'Synced',
      syncing: 'Syncing…',
      pending: 'Pending',
      error: 'Sync error',
    },
    follower: {
      banner: 'This tab is a follower',
      takeover: 'Take control',
      takingOver: 'Taking over…',
      masterBusy: 'Master has an active session',
    },
    offline: {
      banner: 'Offline. Changes will sync when you reconnect.',
    },
    migration: {
      banner: 'You have local data pending migration.',
      action: 'Migrate now',
    },
  },
  settings: {

    title: 'Preferences',
    theme: 'Theme',
    themeDark: 'Dark',
    themeOled: 'OLED',
    themeTeal: 'Teal',
    themeSand: 'Sand',
    themeLight: 'Light',
    themeSage: 'Sage',
    themeWine: 'Wine',
    themeCoral: 'Coral',
    languageHint: 'Select the application language.',
    customizeTheme: 'Customize the interface color palette.',
    changeTheme: 'Change Theme',
    showFrustration: 'Show frustration level',
    showEnergy: 'Show energy level',
    showReadingTime: 'Show reading time',
    showDistractions: 'Show distractions',
    alarmEnabled: 'Enable timer alarm sound',
    keepScreenAwake: 'Keep screen awake during sessions',
    problemTimerMode: 'Problem timer mode',
    problemTimerTimer: 'Timer (count up)',
    problemTimerCountdown: 'Countdown (count down)',
    countdownDuration: 'Duration',
    clearAllDataConfirm: 'Delete ALL your data? This includes subjects, sessions, attempts, reviews, conjectures and settings. Your account stays active.',
    clearAllDataSuccess: 'Data deleted. Your account is still active.',
    clearAllDataPartial: 'Local data deleted, but {n} remote tables failed. Check your connection.',
    language: 'Language',
    shortcuts: 'Keyboard shortcuts',
    shortcutsHint: 'Click a field and press a key. Escape cancels.',
    resetShortcuts: 'Reset to defaults',
    save: 'Save changes',
    cancel: 'Cancel',
    discard: 'Discard changes',
    unsavedWarning: 'You have unsaved changes. Discard them?',
    confirmDiscard: 'Yes, discard',
    dangerZone: 'Danger Zone',
    dangerHint: 'Irreversible actions. Proceed with caution.',
    dangerPurgeLabel: 'Delete all local data',
    dangerPurgeDescription: 'Removes every subject, session, attempt, recall item, and preference stored on this device. This cannot be undone.',
    dangerKeywordInstruction: 'Type {keyword} to confirm.',
    dangerKeywordPlaceholder: 'Type here…',
    dangerPurge: 'Delete everything',
    dangerKeywordMismatch: 'The keyword does not match.',
    backup: 'Backup',
    backupHint: 'Export your data as a compressed JSON file, or restore from a previous backup.',
    export: 'Export data',
    exporting: 'Exporting…',
    exportDone: 'Exported {name} ({size})',
    import: 'Import data',
    importConfirm: 'This will replace all your local data. Continue?',
    importDone: 'Imported {count} records',
    importCancel: 'Cancel',
    importYes: 'Yes, replace',
    importErrorInvalid: 'The file is not a valid backup.',
    importErrorVersion: 'The backup uses an unsupported version.',
    importErrorApp: 'The file is not a StudyQuantifier backup.',
    importErrorDecompress: 'Could not read the file.'
  },
  forecast: {
    title: 'Forecast',
    subtitle: 'Next 30 days of reviews',
    open: 'Forecast',
    dueToday: 'Due today',
    active: 'Active items',
    avgStability: 'Avg stability',
    avgDifficulty: 'Avg difficulty',
    empty: 'No active reviews yet',
    emptyHint: 'Items scheduled for review will appear here.',
    selectHint: 'Tap a day to see its items.',
    selectedDay: 'Selected day',
    noItems: 'No items scheduled for this day',
    reviewNow: 'Review now',
    reviewAllDue: 'Review all due today',
    back: 'Back',
    today: 'Today',
    itemLabel: 'Problem {n}',
    close: 'Close',
    dayAria: '{date}, {count} reviews'
  },
  manage: {
    deleteSubject: 'Delete subject',
    archiveTitle: 'Archive subject',
    archiveIntro: 'The subject and all its contents will be archived.',
    archiveHint: 'Sessions and attempts stay in your history. You can restore it later.',
    confirmArchive: 'Archive',
    cancel: 'Cancel',
    viewArchived: 'Archived ({n})',
    archivedTitle: 'Archived subjects',
    archivedEmpty: 'No archived subjects',
    archivedEmptyHint: 'Subjects you archive will appear here for restoration.',
    archivedHint: 'Restore an archived subject to make it available again.',
    restore: 'Restore',
    countsHeader: 'Will be archived',
    preservedHeader: 'Preserved in history',
    subjects: 'Subjects',
    subtopics: 'Subtopics',
    books: 'Books',
    chapters: 'Chapters',
    sections: 'Sections',
    recallItems: 'Recall items',
    sessions: 'Sessions',
    attempts: 'Attempts'
  },
  lifetime: {
    title: 'Lifetime',
    sessions: 'sessions',
    activeDays: 'active days'
  },
  analytics: {
    title: 'Analytics',
    subtitle: 'Your scientific progress, in numbers and curves.',
    open: 'Analytics',
    empty: 'Not enough data yet',
    emptyHint: 'Create attempts and reviews to see charts here.',
    registered: 'Registered on {date}',
    hoursZero: '—',
    howComputed: 'How is it computed?',
    backlog: {
      title: 'FSRS review backlog',
      dueNow: 'Due now',
      next7d: 'Next 7 days',
      active: 'Active items',
      empty: 'No reviews scheduled yet. Incorrect or unresolved problems are scheduled automatically.',
      cta: 'Go to review'
    },
    summary: {
      totalItems: 'Total items',
      activeItems: 'Active',
      avgStability: 'Avg stability',
      medianStability: 'Median stability',
      avgDifficulty: 'Avg difficulty',
      dueToday: 'Due today',
      totalReviews: 'Reviews'
    },
    scatter: {
      title: 'Stability vs difficulty',
      xAxis: 'Difficulty',
      yAxis: 'Stability'
    },
    curves: {
      title: 'How your memory decays',
      xAxis: 'Days',
      yAxis: 'Retention',
      hint: 'Each line is a card with a different stability (in days). The higher it stays, the longer you remember without reviewing.'
    },
    heatmap: {
      title: 'Activity over the last year',
      legendLess: 'Less',
      legendMore: 'More',
      minutes: '{n} min',
      dayDetail: '{date} · {min} min',
      recentStartBanner: 'You just started building history — only {days} days with activity over the last year.',
      learnMore: 'Learn how we count activity',
      legendTooltip0: 'No activity',
      legendTooltip1: 'Light activity (≤25% of max)',
      legendTooltip2: 'Medium activity (≤50%)',
      legendTooltip3: 'High activity (≤75%)',
      legendTooltip4: 'Maximum activity (100%)'
    },
    kpi: {
      sessionsStudied: 'Sessions studied',
      problemsAttempted: 'Problems attempted',
      timeStudied: 'Time studied',
      correct: 'Correct',
      incorrect: 'Incorrect',
      unresolved: 'Unresolved',
      pendingReviews: 'Pending reviews',
      conjectures: 'Conjectures',
      hoursToday: 'Hours today',
      sessionsTip: 'Finished sessions with the block timer.',
      problemsTip: 'Problem attempts logged in any session.',
      timeStudiedTip: 'Total focus time across all finished sessions.',
      correctTip: 'Attempts marked as correct.',
      incorrectTip: 'Attempts marked as incorrect — scheduled for FSRS review.',
      unresolvedTip: 'Unresolved attempts — scheduled for FSRS review.',
      pendingReviewsTip: 'FSRS review items that are already due and awaiting review.',
      conjecturesTip: 'Saved notes and insights.',
      hoursTodayTip: 'Focus time accumulated today.',
      dailyAvg: 'Daily average',
      dailyAvgTip: 'Average per day with activity. Skips days without sessions.',
      daysLearned: 'Days with activity',
      daysLearnedOf: '{n} days · {pct}%',
      activeTotalLabel: '{n} active items total',
      longestStreak: 'Longest streak',
      currentStreak: 'Current streak',
      days: 'days',
      day: 'day',
      recalled: 'Recalled',
      streak: 'Streak',
      sinceLastSession: 'since last session'
    },
    breakdown: {
      todayTitle: "Today's Breakdown",
      globalTitle: 'Global Breakdown',
      total: 'Total',
      activity30d: 'Activity (Last 30 Days)'
    },
    chart: {
      axes: {
        x: 'Days since review',
        y: 'Recall probability'
      },
      ariaLabel: 'Forgetting curve chart: how recall probability decays over time depending on each card\'s stability.',
      legend: 'Stability (days)'
    }
  },
  history: {
    title: 'History',
    open: 'History',
    noSessions: 'No sessions yet',
    noSessionsHint: 'Finished blocks appear here.',
    loadMore: 'Load more',
    loading: 'Loading…',
    session: 'Session',
    attempts: 'Attempts',
    duration: 'Focus',
    reading: 'Reading',
    distractions: 'Distractions',
    frustration: 'Frustration',
    energy: 'Energy',
    status: 'Status',
    statusFinished: 'Finished',
    statusRunning: 'In progress',
    statusDiscarded: 'Discarded',
    statusPending: 'Pending',
    problem: 'Problem',
    result: 'Result',
    resultCorrect: 'Correct',
    resultIncorrect: 'Incorrect',
    resultUnresolved: 'Unresolved',
    taxonomy: 'Root cause',
    notes: 'Notes',
    edit: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    confirmDelete: 'Delete this record?',
    yes: 'Yes',
    no: 'No',
    minutes: '{n} min',
    noAttempts: 'No attempts recorded for this session',
    attemptsCount: '{n} attempts'
  },
  conjectures: {
    title: 'Conjectures',
    open: 'Conjectures',
    newConjecture: 'New conjecture',
    contentLabel: 'Insight or hypothesis',
    contentPlaceholder: 'Write the intuition or theoretical idea you want to remember…',
    subject: 'Subject',
    subjectNone: 'No subject',
    subtopic: 'Subtopic',
    subtopicNone: 'No subtopic',
    problemNumber: 'Problem number',
    problemNumberPlaceholder: 'e.g. 3.7',
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    delete: 'Delete',
    empty: 'No conjectures yet',
    emptyHint: 'Capture theoretical insights while you study. They will appear here.',
    list: 'All conjectures',
    filterBySubject: 'Filter by subject',
    allSubjects: 'All subjects',
    itemsCount: '{n} items',
    noResults: 'No conjectures match the current filter'
  },
  checklist: {
    title: 'Syllabus checklist',
    open: 'Checklist',
    empty: 'No subjects yet',
    emptyHint: 'Create a subject first to build your checklist.',
    overall: 'Overall',
    completedOf: '{done} / {total}',
    toggleComplete: 'Toggle completion',
    expandAll: 'Expand all',
    collapseAll: 'Collapse all',
    clearCompleted: 'Reset all',
    itemSubject: 'Subject',
    itemSubtopic: 'Subtopic',
    itemBook: 'Book',
    itemChapter: 'Chapter',
    itemSection: 'Section',
    addSyllabus: 'Add Syllabus',
    tabManual: 'Manual',
    tabImport: 'AI Import',
    rootNamePlaceholder: 'Course Name (e.g. Mathematics)',
    nodeNamePlaceholder: 'Node Name',
    typePlaceholder: 'Type (e.g. Course)',
    subItemTypePlaceholder: 'Type (e.g. Module)',
    addSubItem: 'Add Sub-item',
    cancel: 'Cancel',
    create: 'Create',
    rootNameError: 'Please enter a root name before creating.',
    trashWarning: 'Restoring an item will also restore all its sub-items. Deleting it permanently will remove all sub-items as well.',
    restore: 'Restore',
    deletePerm: 'Delete Permanently',
    deleteConfirm: 'Are you sure? This will permanently delete this item and all its sub-items from the cloud. This cannot be undone.'
  },
  goals: {
    title: 'Goals & streak',
    open: 'Goals',
    streak: 'Streak',
    streakDays: '{n} days',
    dayShort: '{n} d',
    longest: 'Longest',
    todayProgress: 'Today',
    weekProgress: 'This week',
    minutesOf: '{done} / {target} min',
    dailyTarget: 'Daily target (min)',
    weeklyTarget: 'Weekly target (min)',
    activeDays: 'Active days',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved',
    last14Days: 'Last 14 days',
    noSessions: 'No finished sessions yet',
    inGrace: 'Grace day — keep going',
    streakBroken: 'No streak yet',
    weekdays: {
      sun: 'Sun',
      mon: 'Mon',
      tue: 'Tue',
      wed: 'Wed',
      thu: 'Thu',
      fri: 'Fri',
      sat: 'Sat'
    }
  },
  sleep: {
    title: 'Sleep log',
    open: 'Sleep',
    bedtime: 'Bedtime',
    wakeTime: 'Wake time',
    quality: 'Quality',
    qualityLow: 'Poor',
    qualityHigh: 'Excellent',
    duration: 'Duration',
    hoursShort: '{h}h {m}m',
    save: 'Save',
    saving: 'Saving…',
    update: 'Update',
    cancel: 'Cancel',
    delete: 'Delete',
    noRecords: 'No sleep records yet',
    noRecordsHint: 'Log your sleep to track quality and patterns.',
    recentRecords: 'Recent records',
    circadianHint: 'Bedtimes before 18:00 are grouped with the previous day.',
    errorInvalidOrder: 'Wake time must be after bedtime.',
    errorTooShort: 'Duration must be at least 30 minutes.',
    errorTooLong: 'Duration cannot exceed 24 hours.',
    durationLabel: '{h} h {m} min',
    qualityLabel: 'Q{q}'
  },
  errors: {
    generic: 'An unexpected error occurred.',
    notFound: 'Resource not found.',
    network: 'Offline. Changes will be saved locally.',
    storageFull: 'Local storage is full.',
    unknown: 'Unknown error.'
  }
} as const;